import { AlertCircle, AlertTriangle, HandCoins, Inbox, Info, Save, Send, TrendingUp } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaChoiceCardField } from "@/shared/components/forms/ColocaChoiceCardField";
import { ColocaComboboxField } from "@/shared/components/forms/ColocaComboboxField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { formatRail } from "@/shared/utils/railDisplay";

import {
  corridorCellKey,
  corridorTripleKey,
  declaredDirectionsFor,
  OPERATIONAL_CORRIDORS,
  type CorridorIdentity,
} from "../../constants/paymentRoutes.constants";
import { useFeeLabels } from "../../hooks/useFeeLabels";
import { useRouteMutations } from "../../hooks/useRouteMutations";
import { EconomicModel, PaymentDirection, type PaymentFeeRail } from "../../types/paymentFees.types";
import type {
  CorridorRouteCell,
  CreatePaymentRoutePayload,
  ProviderRailRoute,
} from "../../types/paymentRoutes.types";
import { corridorCellLabel } from "../../utils/routeFormat";
import { activeRouteCountByProvider } from "../../utils/routeMatrix";
import { ProviderChoiceField } from "./ProviderChoiceField";
import { buildRouteFormSchema, ROUTE_FORM_DEFAULTS, type RouteFormValues } from "./routeFormSchema";

export interface CreateRouteIntent {
  /** `"<country>|<rail>|<currency>"` of the cell the dialog was opened from, when it was. */
  corridorKey?: string;
  direction?: PaymentDirection;
}

export interface CreateRouteDialogProps {
  intent: CreateRouteIntent;
  open: boolean;
  onClose: () => void;
  onCreated: (route: ProviderRailRoute, corridorLabel: string) => void;
  /** Every cell — the occupancy checks read them. */
  cells: CorridorRouteCell[];
  /** Every route — the "routes N corridors" figure of each provider option. */
  routes: ProviderRailRoute[];
  /** Escape hatch when the corridor is already routed: the only legal path is delete-then-create. */
  onSwitchRequest: (cell: CorridorRouteCell) => void;
}

/**
 * Authoring dialog for a route — one of the API's only two mutations.
 *
 * Structure follows what a route actually is: **which** corridor and direction, **who** executes it, and
 * **who pays the provider cost**. Two backend outcomes are prevented structurally rather than reported
 * afterwards: an occupied corridor (the unique index would answer `400 route_exists`, and it ignores
 * `is_active`) and a provider that does not declare the corridor (the payout would strand as `CREATED` after
 * reserving funds).
 *
 * `is_active` is not offered. The backend defaults it to `true`, and a `false` row would create a corridor
 * that neither operates nor accepts a replacement, with no endpoint to reactivate it.
 */
export function CreateRouteDialog({
  intent,
  open,
  onClose,
  onCreated,
  cells,
  routes,
  onSwitchRequest,
}: CreateRouteDialogProps) {
  const { t } = useTranslations("paymentRoutes");
  const labels = useFeeLabels();
  const { isSubmitting, error: submitError, clearError, createRoute } = useRouteMutations();

  const schema = useMemo(
    () =>
      buildRouteFormSchema({
        corridorRequired: t("validation_corridor_required"),
        providerRequired: t("validation_provider_required"),
      }),
    [t],
  );

  const defaults = useMemo<RouteFormValues>(
    () => ({
      ...ROUTE_FORM_DEFAULTS,
      corridorKey: intent.corridorKey ?? "",
      direction: intent.direction ?? "",
    }),
    [intent],
  );

  const form = useZodForm(schema, defaults);

  // The dialog is mounted per intent; a re-open with a different one must not keep stale values.
  const lastIntentRef = useRef(intent);
  useEffect(() => {
    if (lastIntentRef.current === intent) return;
    lastIntentRef.current = intent;
    form.reset(defaults);
    clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent, defaults]);

  const values = form.watch();

  const corridorOptions = useMemo(
    () =>
      OPERATIONAL_CORRIDORS.map((corridor) => ({
        value: corridorTripleKey(corridor),
        label: `${corridor.country} · ${formatRail(corridor.rail)} · ${corridor.currency}`,
      })),
    [],
  );

  /**
   * Directions some adapter can actually execute on the picked corridor. The others are disabled rather than
   * hidden: the pair is the corridor's shape, and a silently missing option reads as a rendering bug.
   */
  const declaredDirections = useMemo(() => {
    if (!values.corridorKey) return [PaymentDirection.PAYIN, PaymentDirection.PAYOUT];
    const [country, rail, currency] = values.corridorKey.split("|");
    return declaredDirectionsFor({ country, rail: rail as PaymentFeeRail, currency });
  }, [values.corridorKey]);

  /** The corridor being routed, once both halves of its identity are picked. */
  const corridor = useMemo<CorridorIdentity | null>(() => {
    if (!values.corridorKey) return null;
    if (values.direction !== PaymentDirection.PAYIN && values.direction !== PaymentDirection.PAYOUT) {
      return null;
    }
    const [country, rail, currency] = values.corridorKey.split("|");
    const match = OPERATIONAL_CORRIDORS.find(
      (candidate) =>
        candidate.country === country && candidate.rail === rail && candidate.currency === currency,
    );
    return match ? { ...match, direction: values.direction } : null;
  }, [values.corridorKey, values.direction]);

  const targetCell = useMemo(
    () => (corridor ? (cells.find((cell) => cell.key === corridorCellKey(corridor)) ?? null) : null),
    [cells, corridor],
  );

  const occupiedBy = targetCell?.route ?? null;
  const isBlockedByInactive = occupiedBy != null && !occupiedBy.is_active;
  const corridorCountByProvider = useMemo(() => activeRouteCountByProvider(routes), [routes]);

  const canSubmit = corridor != null && occupiedBy == null && !!values.providerCode;

  const handleSubmit = async (submitted: RouteFormValues) => {
    if (!corridor || occupiedBy) return;

    const payload: CreatePaymentRoutePayload = {
      rail: corridor.rail,
      country: corridor.country,
      currency: corridor.currency,
      direction: corridor.direction,
      provider_code: submitted.providerCode,
      economic_model: submitted.economicModel,
    };

    const created = await createRoute(payload);
    if (created) onCreated(created, corridorCellLabel(corridor, labels.directionShort));
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onClose();
      }}
      title={t("create_title")}
      description={t("create_subtitle")}
      size="lg"
      mobileFullscreen
    >
      <ColocaForm form={form} onSubmit={handleSubmit} className="space-y-0">
        {/* The body owns the scroll (ColocaDialog does not cap its own height); below `md` the dialog becomes a
            fullscreen drawer that scrolls itself, hence the `md:` prefix. */}
        <div className="flex flex-col md:max-h-[62vh] lg:flex-row lg:gap-0">
          <div className="-mr-1 flex flex-1 flex-col gap-6 pr-1 md:overflow-y-auto lg:pr-6">
            {/* 1 — which corridor. One pick fills rail + currency; the direction is the second half of it. */}
            <section className="space-y-3">
              <SectionTitle>{t("create_section_corridor")}</SectionTitle>
              <ColocaComboboxField
                name="corridorKey"
                label={t("create_corridor_label")}
                items={corridorOptions}
                placeholder={t("create_corridor_placeholder")}
                description={t("create_corridor_helper")}
              />
              <ColocaChoiceCardField
                name="direction"
                label={t("create_direction_label")}
                columns={2}
                items={[
                  {
                    value: PaymentDirection.PAYIN,
                    label: labels.direction[PaymentDirection.PAYIN],
                    description: t("direction_payin_description"),
                    icon: <Inbox className="size-5 text-success" />,
                    disabled: !declaredDirections.includes(PaymentDirection.PAYIN),
                  },
                  {
                    value: PaymentDirection.PAYOUT,
                    label: labels.direction[PaymentDirection.PAYOUT],
                    description: t("direction_payout_description"),
                    icon: <Send className="size-5 text-primary" />,
                    disabled: !declaredDirections.includes(PaymentDirection.PAYOUT),
                  },
                ]}
              />

              {occupiedBy && targetCell && (
                <ColocaAlert
                  variant={isBlockedByInactive ? "warning" : "destructive"}
                  icon={AlertTriangle}
                  text={isBlockedByInactive ? t("create_warn_blocked") : t("create_warn_exists")}
                  actions={
                    !isBlockedByInactive && (
                      <ColocaButton
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={() => onSwitchRequest(targetCell)}
                      >
                        {t("action_switch")}
                      </ColocaButton>
                    )
                  }
                />
              )}
            </section>

            {/* 2 — who executes it. Only providers whose adapter declares this corridor. */}
            <section className="space-y-3">
              <SectionTitle>{t("create_section_provider")}</SectionTitle>
              <ProviderChoiceField
                name="providerCode"
                corridor={corridor}
                corridorCountByProvider={corridorCountByProvider}
              />
            </section>

            {/* 3 — who pays the provider cost. Drives the net formula and the ledger legs. */}
            <section className="space-y-3">
              <SectionTitle>{t("create_section_model")}</SectionTitle>
              <ColocaChoiceCardField
                name="economicModel"
                label={t("create_model_label")}
                description={t("create_model_helper")}
                columns={1}
                items={[
                  {
                    value: EconomicModel.PASS_THROUGH,
                    label: labels.model[EconomicModel.PASS_THROUGH],
                    description: labels.modelHelp[EconomicModel.PASS_THROUGH],
                    icon: <TrendingUp className="size-5 text-success" />,
                  },
                  {
                    value: EconomicModel.ABSORBED,
                    label: labels.model[EconomicModel.ABSORBED],
                    description: labels.modelHelp[EconomicModel.ABSORBED],
                    icon: <HandCoins className="size-5 text-warning" />,
                  },
                ]}
              />
            </section>

            {submitError && <ColocaAlert variant="destructive" icon={AlertCircle} text={submitError} />}
          </div>

          {/* Consequence panel — the mirror of the fee dialog's preview: what this row will actually do. */}
          <aside className="mt-6 shrink-0 lg:mt-0 lg:w-72 lg:border-l lg:border-border lg:pl-6">
            <div className="flex flex-col gap-3 rounded-xl border border-border bg-muted/40 p-4 lg:border-0 lg:bg-transparent lg:p-0">
              <SectionTitle>{t("create_summary_title")}</SectionTitle>
              {corridor ? (
                <>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <ColocaBadge variant="default">{corridor.country}</ColocaBadge>
                    <ColocaBadge variant="secondary" className="font-sans">
                      {formatRail(corridor.rail)}
                    </ColocaBadge>
                    <span className="font-mono text-xs text-muted-foreground">{corridor.currency}</span>
                    <ColocaBadge variant="default" className="font-sans">
                      {labels.direction[corridor.direction]}
                    </ColocaBadge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t("create_summary_sentence", {
                      corridor: corridorCellLabel(corridor, labels.directionShort),
                      model: labels.model[values.economicModel].toLowerCase(),
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground">{labels.modelHelp[values.economicModel]}</p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">{t("create_summary_pick_corridor")}</p>
              )}
              <ColocaAlert variant="secondary" icon={Info} text={t("create_next_step")} />
            </div>
          </aside>
        </div>

        <div className="mt-4 flex justify-end gap-2 border-t border-border pt-3">
          <ColocaButton type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t("cancel")}
          </ColocaButton>
          <ColocaButton
            type="submit"
            variant="primary"
            icon={Save}
            loading={isSubmitting}
            disabled={!canSubmit}
          >
            {t("submit")}
          </ColocaButton>
        </div>
      </ColocaForm>
    </ColocaDialog>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-mono text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </h3>
  );
}
